import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Upload, Avatar, Row, Col, Typography } from 'antd';
import { UserOutlined, UploadOutlined, MailOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, setUser } from '../store/userSlice';
import { auth } from '../services/api';
import Navbar from '../components/layout/Navbar';
import ChangePasswordForm from '../components/auth/ChangePasswordForm';
import type { RcFile } from 'antd/es/upload/interface';
import type { User } from '../types/user';

const { Text } = Typography;

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f2f5;
  padding-top: 64px;
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px;
`;

const ProfileCard = styled(Card)`
  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  .ant-card-body {
    padding: 32px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-right: 32px;
  border-right: 1px solid #f0f0f0;
  height: 100%;
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
  
  .ant-avatar {
    width: 120px;
    height: 120px;
    margin-bottom: 16px;
  }
`;

const MembershipInfo = styled.div`
  text-align: center;
  
  .status-tag {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    margin-bottom: 8px;
  }
  
  .expiry-date {
    font-size: 14px;
    color: #666;
  }
`;

const RightSection = styled.div`
  padding-left: 32px;
`;

const InfoItem = styled.div`
  margin-bottom: 24px;
  
  .label {
    color: #666;
    margin-bottom: 8px;
  }
  
  .value {
    color: #1f2937;
    font-size: 15px;
  }
`;

const UserProfile: React.FC = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const fetchAvatar = async (avatarPath: string | undefined | null) => {
    if (!avatarPath) return;
    try {
      const filename = avatarPath.split('/').pop();
      if (filename) {
        const url = await auth.getAvatar(filename);
        setAvatarUrl(url);
      }
    } catch (error) {
      console.error('获取头像失败:', error);
    }
  };

  useEffect(() => {
    if (user?.avatarUrl) {
      fetchAvatar(user.avatarUrl);
    }
  }, [user?.avatarUrl]);

  const handleAvatarUpload = async (file: RcFile) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await auth.updateAvatar(formData);
      await fetchAvatar(response.avatarUrl);
      const updatedUser: User = { ...user!, avatarUrl: response.avatarUrl };
      dispatch(setUser(updatedUser));
      message.success('头像更新成功');
    } catch (error: any) {
      message.error('头像上传失败，请重试');
    }
    return false;
  };

  return (
    <>
      <Navbar />
      <PageContainer>
        <ContentWrapper>
          <ProfileCard title="个人信息">
            <Row gutter={0}>
              <Col span={8}>
                <LeftSection>
                  <AvatarSection>
                    <Avatar 
                      size={120}
                      icon={<UserOutlined />} 
                      src={avatarUrl}
                    />
                    <Upload
                      showUploadList={false}
                      beforeUpload={handleAvatarUpload}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />}>更换头像</Button>
                    </Upload>
                  </AvatarSection>
                  
                  {user && (
                    <MembershipInfo>
                      <div className="status-tag" style={{ 
                        backgroundColor: user.isPremium ? '#e6f7ff' : '#f5f5f5',
                        color: user.isPremium ? '#1890ff' : '#666'
                      }}>
                        {user.isPremium ? '专业版会员' : '免费用户'}
                      </div>
                      {user.isPremium && user.premiumExpireAt && (
                        <div className="expiry-date">
                          会员有效期至：{new Date(user.premiumExpireAt).toLocaleDateString()}
                        </div>
                      )}
                    </MembershipInfo>
                  )}
                </LeftSection>
              </Col>
              
              <Col span={16}>
                <RightSection>
                  <InfoItem>
                    <div className="label">
                      <MailOutlined style={{ marginRight: 8 }} />
                      邮箱
                    </div>
                    <div className="value">
                      {user?.email}
                    </div>
                  </InfoItem>

                  <ChangePasswordForm />
                </RightSection>
              </Col>
            </Row>
          </ProfileCard>
        </ContentWrapper>
      </PageContainer>
    </>
  );
};

export default UserProfile; 